import React, { useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { useNavigate, useLocation } from 'react-router-dom';
import { db } from '../firebase';
import AppLinksFooter from './AppLinksFooter';
import { isMobilePlatform } from '../utils/platform';

const DEFAULT_CONTENT = `SAFETY & PRIVACY

Effective Date: June 3, 2026
Last Updated: June 3, 2026

At GuffPuff, your safety and privacy come first. This page explains the tools, practices, and guidelines we have in place to help keep you safe while using GuffPuff.

1. OUR COMMITMENT TO SAFETY

We are committed to providing a respectful and secure environment for all users. We continuously work to prevent harassment, abuse, and harmful behavior on the platform.

2. STAYING SAFE ON GUFFPUFF

- Never share sensitive personal information (home address, financial details, passwords) with other users.
- Be cautious when interacting with people you do not know.
- Use a strong, unique password and keep your login credentials private.
- Report any user or content that makes you feel unsafe.

3. BLOCKING AND REPORTING

You can block any user at any time to stop them from contacting you. You can also report users or content that violate our community guidelines. Our moderation team reviews reports and takes appropriate action, which may include warnings, suspensions, or permanent bans.

4. PRIVACY CONTROLS

You control what you share. In your settings you can manage profile visibility, who can message you, and what information is shown to other users.

5. PROTECTING MINORS

GuffPuff is intended for adults. We do not allow users under the age of 18. If you believe a minor is using the platform, please report it immediately.

6. CONTACT US

If you have any safety or privacy concerns, please reach out to us:
Safety Team: safety@guffpuff.com
Privacy Team: privacy@guffpuff.com
Support: support@guffpuff.com
Website: www.guffpuff.com`;

const SafetyAndPrivacyPage = () => {
  const [content, setContent] = useState('');
  const [title, setTitle] = useState('Safety & Privacy');
  const [lastUpdated, setLastUpdated] = useState('');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const hideBack = isMobilePlatform(location.search);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const docRef = doc(db, 'legal_content', 'safety_and_privacy');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          setTitle(data.title || 'Safety & Privacy');
          setContent(data.content || DEFAULT_CONTENT);
          if (data.lastUpdated) {
            setLastUpdated(
              new Date(data.lastUpdated.toDate ? data.lastUpdated.toDate() : data.lastUpdated).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })
            );
          }
        } else {
          setContent(DEFAULT_CONTENT);
        }
      } catch (err) {
        console.error('Error fetching Safety & Privacy:', err);
        setContent(DEFAULT_CONTENT);
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <div style={{ background: 'linear-gradient(to right, #22d3ee, #0891b2)', position: 'relative' }} className="py-12 px-6">
        <div className="max-w-4xl mx-auto text-center">
          {!hideBack && (
          <button
            onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/admin/login'))}
            style={{ position: 'absolute', top: '24px', left: '24px', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', color: 'rgba(255,255,255,0.9)', fontSize: '14px', fontWeight: 500, padding: 0 }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
          )}
          <img
            src={`${process.env.PUBLIC_URL}/images/logo.png`}
            alt="GUFFPUFF"
            className="h-16 mx-auto mb-4"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <h1 className="text-3xl font-bold text-white">{title}</h1>
          {lastUpdated && (
            <p className="text-cyan-100 mt-2 text-sm">Last updated: {lastUpdated}</p>
          )}
        </div>
      </div>

      <div className="max-w-4xl mx-auto py-10 px-6">
        <div className="bg-white rounded-2xl shadow-sm p-8">
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="w-10 h-10 border-4 border-cyan-400 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : content ? (
            <div
              className="prose prose-gray max-w-none text-gray-700 leading-relaxed"
              style={{ whiteSpace: 'pre-wrap', lineHeight: '1.8', fontSize: '15px' }}
            >
              {content}
            </div>
          ) : (
            <p className="text-gray-400 text-center py-20">No content available yet.</p>
          )}
        </div>
      </div>

      <div className="text-center py-6 text-sm text-gray-400 space-y-3">
        <AppLinksFooter variant="muted" />
        <p>&copy; {new Date().getFullYear()} GuffPuff. All rights reserved.</p>
      </div>
    </div>
  );
};

export default SafetyAndPrivacyPage;
